import type {Accept} from 'react-dropzone-esm';
import type {
  FeatureFlagEnum,
  FormFieldTypeValidationEnum,
  SelectOption,
} from 'src/api/ProactApiClient';

export interface IFormFieldOptions {
  toolTipText?: string;
  isQuestionMarkAfterLabelDisabled?: boolean;
  isColonAfterLabelDisabled?: boolean;
  isBorderBottomDisabled?: boolean;

  // Upload options
  allowMultiple?: boolean;
  allowedFileTypes?: string[] | Accept;
  maxFileSize?: number;
  showFileSizeWarning?: boolean;
  isHideUploadSuccessEnabled?: boolean;

  // Scale options
  leastValueText?: string;
  mostValueText?: string;

  // Switch options
  positiveText?: string;
  negativeText?: string;
  isNADisabled?: boolean;

  isDefaultToFalseDisabled?: boolean;

  // Dropdown options
  optionType?: string;
  assetTypeId?: number;
  hideSelectAll?: boolean;
}
export interface IFormFieldTypeGroup {
  selectModal?: boolean;
  select?: boolean;
  singleSelect?: boolean;
  autocomplete?: boolean;
  textBox?: boolean;
  textArea?: boolean;
  datePicker?: boolean;
  richContent?: boolean;
  scale?: boolean;
  paragraph?: boolean;
  phoneNumber?: boolean;
  documentUpload?: boolean;
  singleDocumentUpload?: boolean;
  imageUpload?: boolean;
  imageUpload2?: boolean;
  switch?: boolean;
  heading?: boolean;
  divider?: boolean;
  subform?: boolean;
  personSelectModal?: boolean;
  signature?: boolean;
  options?: IFormFieldOptions;
}

export interface VisibilityRuleLegacy {
  offlineId?: string | undefined;
  fieldName: string;
  fieldValue: string;
  visibility: boolean;
}

export interface IFormFieldMetadata {
  validationType?: FormFieldTypeValidationEnum;
  defaultValue?: any;
  slugText?: string;
  staticOnly?: boolean;
  helpText?: string;
  listOrder?: number;
  numberOfLines?: number;
  multiline?: boolean;
  description?: string;
  isDefaultHidden?: boolean;
  visibilityRules?: VisibilityRuleLegacy[];
  cache?: any;
}
export interface ISelectOption {
  id: string | number;
  label: string;
  text?: string;
  color?: string;
  description?: string;
}

export interface IListItem {
  label: string;
  value: string;
}

export interface IFeatureFlagSelectOption {
  id: string | number;
  label: string;
  text?: string;
  featureFlag?: FeatureFlagEnum;
}

type HTMLInputTypeAttribute =
  | 'button'
  | 'checkbox'
  | 'color'
  | 'date'
  | 'datetime-local'
  | 'email'
  | 'file'
  | 'hidden'
  | 'image'
  | 'month'
  | 'number'
  | 'password'
  | 'radio'
  | 'range'
  | 'reset'
  | 'search'
  | 'submit'
  | 'tel'
  | 'text'
  | 'time'
  | 'url'
  | 'week';

export interface IFormField {
  invariantId?: string;
  autoComplete?: boolean;
  htmlInputType?: HTMLInputTypeAttribute;
  requiresFeatureFlag?: FeatureFlagEnum;
  maxLength?: number;
  multiline?: boolean;
  cache?: any;
  searchKeys?: string[];
  inputAriaLabel?: string;
  value?: any;
  id?: number;
  version?: number;
  name: string;
  htmlName: string;
  metadata?: IFormFieldMetadata;
  type: string;
  label: string;
  shortLabel?: string;
  active: boolean;
  required?: boolean;
  isVisible?: boolean;
  ariaLabel?: string;
  fullWidth?: boolean;
  passwordRequirements?: string;
  objectKey?: any;
  cssInput?: any;
  modalTitleText?: string;
  isPassword?: boolean;
  invalidId?: string;
  invalidFields?: string[];
  cssBorderRadius?: any;
  disabled?: boolean;
  placeholderText?: string;
  options?: IFormFieldOptions;
  formFieldType?: FormFieldTypeLegacyEnum;
  hasValidationType?: any;
  formFieldTypeGroup?: IFormFieldTypeGroup;
  selectOptions?: ISelectOption[] | SelectOption[];
  subFormType?: number;
  subFormDefinitionId?: number;
  subFormDefinitionVersion?: number;
  hideSelectAll?: boolean;

  isComplianceEventField?: boolean;
  complianceEventTriggerValue?: string;
  complianceStandardIds?: ISelectOption[];

  listOrder?: number;
  viewInEdit?: boolean;
  readOnlyInEdit?: boolean;
}

export interface IValidationResponse {
  isValid: boolean;
  validationMessage?: string;
  validationExample?: string;
}

export interface IFormData {
  [key: string]: any;
}

export interface IFormFieldDropDownOptions {
  id: string;
  label: string;
  text?: string;
  orderId: number;
  color?: string;
}

export enum FormFieldTypeLegacyEnum {
  Invalid = 0,
  TextInput = 1,
  Date = 2,
  Time = 3,
  DateTime = 4,
  Dropdown = 5,
  Checkbox = 6,
  RichText = 7,
  ImageUpload = 8,
  FileUpload = 9,
  Paragraph = 10,
  Divider = 11,
  Subform = 12,
  Scale = 13,
  Heading = 14,
  DropdownAutocomplete = 15,
  DropdownModal = 16,
  DropdownPersonnelModal = 17,
  SingleFileUpload = 18,
  Signature = 19,
  PhoneNumber = 20,
  AssetModal = 21,
  DropdownSinglePersonnel = 22,
  Switch = 23,
}
